import type { GeoCoordinates } from "../../components/locationPopup/useGeoLocator";
import { cloudsLayerConfig } from "../../config/products/cloudsLayerConfig";
import { precipitationLayerConfig } from "../../config/products/precipitationLayerConfig";
import type { TileLayerName } from "../../config/products/types";
import { windLayerConfig } from "../../config/products/windLayerConfig";
import type { TimeStepsSet } from "../../data/hooks/types";
import { useGlobalState } from "../../state/hooks/useGlobalState";
import { useBackendAPIRequest } from "../matlocq/useBackendAPIRequest";
import { combineW3Products } from "./combineW3Products";
import { apiW3ProductToW3Product } from "./mapper";
import type { ApiW3Product } from "./types/api";
import type { W3Product } from "./types/data";
import { useGeoCoordinatesForMultiModelRequests } from "./useGeoCoordinatesForMultiModelRequests";

export const useLayerDataQuery = (layerNames: TileLayerName[]) => {
    const { activeWorldRegion } = useGlobalState();
    const geoLocationForMultiModelRequests =
        useGeoCoordinatesForMultiModelRequests();

    const precipitationNearcastUrl =
        precipitationLayerConfig.dataSources[activeWorldRegion].nearcast;

    const {
        data: precipitationNearcastData,
        isLoading: precipitationNearcastIsLoading,
        error: precipitationNearcastError,
    } = useBackendAPIRequest<ApiW3Product, W3Product | undefined>(
        precipitationNearcastUrl,
        (responseJson) =>
            apiW3ProductToW3Product(
                "precipitation",
                precipitationNearcastUrl,
                responseJson,
                true,
            ),
        {
            skip: !layerNames.includes("precipitation"),
        },
    );

    const precipitationForecastUrl =
        precipitationLayerConfig.dataSources[activeWorldRegion].forecast;

    const precipitationForecastUrlWithCoordinates =
        replaceLngLatPlaceholdersInUrl(
            precipitationForecastUrl,
            geoLocationForMultiModelRequests,
        );

    const {
        data: precipitationForecastData,
        isLoading: precipitationForecastIsLoading,
        error: precipitationForecastError,
    } = useBackendAPIRequest<ApiW3Product, W3Product | undefined>(
        precipitationForecastUrlWithCoordinates,
        (responseJson) =>
            apiW3ProductToW3Product(
                "precipitation",
                precipitationForecastUrl,
                responseJson,
                true,
            ),
        {
            skip: !layerNames.includes("precipitation"),
            // Forecast status file urls will not be cache for every request location.
            // Based on the first requested location it's "decided" which forecast model status file (=forecast model)
            // will be used for this and any further selected location as per ticket specification.
            requestCacheKey: precipitationForecastUrl,
        },
    );

    const cloudsNearcastUrl =
        cloudsLayerConfig.dataSources[activeWorldRegion].nearcast;

    const {
        data: cloudsNearcastData,
        isLoading: cloudsNearcastIsLoading,
        error: cloudsNearcastError,
    } = useBackendAPIRequest<ApiW3Product, W3Product | undefined>(
        cloudsNearcastUrl,
        (responseJson) =>
            apiW3ProductToW3Product(
                "clouds",
                cloudsNearcastUrl,
                responseJson,
                true,
            ),
        {
            skip: !layerNames.includes("clouds"),
        },
    );

    const cloudsForecastUrl =
        cloudsLayerConfig.dataSources[activeWorldRegion].forecast;

    const cloudsForecastUrlWithCoordinates = replaceLngLatPlaceholdersInUrl(
        cloudsForecastUrl,
        geoLocationForMultiModelRequests,
    );

    const {
        data: cloudsForecastData,
        isLoading: cloudsForecastIsLoading,
        error: cloudsForecastError,
    } = useBackendAPIRequest<ApiW3Product, W3Product | undefined>(
        cloudsForecastUrlWithCoordinates,
        (responseJson) =>
            apiW3ProductToW3Product(
                "clouds",
                cloudsForecastUrl,
                responseJson,
                true,
            ),
        {
            skip: !layerNames.includes("clouds"),
            // Forecast status file urls will not be cache for every request location.
            // Based on the first requested location it's "decided" which forecast model status file (=forecast model)
            // will be used for this and any further selected location as per ticket specification.
            requestCacheKey: cloudsForecastUrl,
        },
    );

    const windUrl = windLayerConfig.dataSources[activeWorldRegion].nearcast;

    const {
        data: windW3Product,
        isLoading: windIsLoading,
        error: windError,
    } = useBackendAPIRequest<ApiW3Product, W3Product | undefined>(
        windUrl,
        (responseJson) =>
            apiW3ProductToW3Product("wind", windUrl, responseJson, true),
        {
            skip: !layerNames.includes("wind"),
        },
    );

    const isError =
        precipitationNearcastError !== undefined ||
        precipitationForecastError !== undefined ||
        cloudsNearcastError !== undefined ||
        cloudsForecastError !== undefined ||
        windError !== undefined;

    const isLoading =
        precipitationNearcastIsLoading ||
        precipitationForecastIsLoading ||
        cloudsNearcastIsLoading ||
        cloudsForecastIsLoading ||
        windIsLoading;

    const shownLayersData: Partial<
        Record<"precipitation" | "wind" | "clouds" | "windGust", TimeStepsSet>
    > = {};

    if (
        precipitationNearcastData !== undefined &&
        precipitationForecastData !== undefined
    ) {
        const precipitationData = combineW3Products(
            activeWorldRegion,
            precipitationNearcastData,
            precipitationForecastData,
        );

        if (precipitationData !== undefined) {
            shownLayersData.precipitation = precipitationData;
        }
    }

    if (cloudsNearcastData !== undefined && cloudsForecastData !== undefined) {
        const cloudsData = combineW3Products(
            activeWorldRegion,
            cloudsNearcastData,
            cloudsForecastData,
        );

        if (cloudsData !== undefined) {
            shownLayersData.clouds = cloudsData;
        }
    }

    if (windW3Product !== undefined) {
        const windData = combineW3Products(activeWorldRegion, windW3Product);

        if (windData !== undefined) {
            shownLayersData.wind = windData;
            shownLayersData.windGust = windData;
        }
    }

    return { shownLayersData, isLoading, isError };
};

function replaceLngLatPlaceholdersInUrl(
    url: string,
    selectedLocation: GeoCoordinates | undefined,
) {
    if (selectedLocation === undefined) return url;

    return url
        .replace("{longitude}", selectedLocation.lng.toFixed(5))
        .replace("{latitude}", selectedLocation.lat.toFixed(5));
}
